import api from '../../../lib/api';

/**
 * Explainer import (PRD v2 §5.15) — the wire shapes the two admin routes
 * return, and the calls the modals make. The backend does all the parsing,
 * matching and writing; the client only picks files and reads the preflight.
 */

export interface ExplainerFieldPreview {
  column: string;
  label: string;
  current: string | null;
  incoming: string | null;
  action: 'replace' | 'keep' | 'clear' | 'empty';
}

export interface ExplainerPreflight {
  ok: boolean;
  blocking: string[];
  warnings: string[];
  measure: { id: string; sos_initiative_number: string | null; status: string; publication_status: string };
  designation: { current: string | null; incoming: string | null; action: 'set' | 'keep' | 'conflict' };
  version: string | null;
  fields: ExplainerFieldPreview[];
  blueBook: Array<{ section: string; label: string; incoming: string | null }>;
  documents: string[];
}

export interface ExplainerImportSummary {
  initiative_id: string;
  label: string;
  version: string | null;
  record_id: string | null;
  designation: string | null;
  fields_written: string[];
  fields_cleared: string[];
  fields_kept: string[];
  blue_book_sections: string[];
  documents: Array<{ id: string; label: string; original_name: string; sha256: string }>;
  publication_status: string;
}

export type BulkResultStatus = 'matched' | 'unmatched' | 'conflict' | 'imported' | 'failed';

export interface ExplainerBulkResult {
  prefix: string;
  files: { fields: boolean; record: boolean; pdf: boolean };
  version?: string | null;
  designation?: string | null;
  filing_number?: string | null;
  initiative?: { id: string; label: string; sos_initiative_number: string | null; status: string; publication_status: string };
  status: BulkResultStatus;
  reason?: string;
  preflight?: ExplainerPreflight;
  summary?: ExplainerImportSummary;
}

export interface ExplainerBulkResponse {
  initiative_cycle: string;
  dry_run: boolean;
  ignored: string[];
  results: ExplainerBulkResult[];
}

export interface ExplainerBundle {
  fields: File | null;
  record: File | null;
  pdf: File | null;
}

/** Pick the three bundle files out of any selection (a folder, or a few files). */
export function pickBundleFiles(files: File[]): ExplainerBundle {
  const bundle: ExplainerBundle = { fields: null, record: null, pdf: null };
  for (const file of files) {
    const name = file.name.toLowerCase();
    if (name.endsWith('-fields.json')) bundle.fields = bundle.fields ?? file;
    else if (/-record-[^.]+\.json$/.test(name)) bundle.record = bundle.record ?? file;
    else if (/-record-[^.]+\.pdf$/.test(name)) bundle.pdf = bundle.pdf ?? file;
  }
  return bundle;
}

/** The files the bulk route will consider (the rest of a folder is noise). */
export function selectBundleFiles(files: File[]): File[] {
  return files.filter((f) => /-v\d+(?:\.\d+)*-(fields\.json|record-[^.]+\.(?:json|pdf))$/i.test(f.name));
}

const bundleForm = (bundle: ExplainerBundle, extra: Record<string, string>) => {
  const form = new FormData();
  if (bundle.fields) form.append('fields', bundle.fields);
  if (bundle.record) form.append('record', bundle.record);
  if (bundle.pdf) form.append('pdf', bundle.pdf);
  for (const [k, v] of Object.entries(extra)) form.append(k, v);
  return form;
};

export async function preflightExplainer(initiativeId: string, bundle: ExplainerBundle, keepFields: string[]): Promise<ExplainerPreflight> {
  const res = await api.post(
    `/admin/ballot-initiatives/${initiativeId}/explainer-import`,
    bundleForm(bundle, { dry_run: 'true', keep_fields: keepFields.join(',') })
  );
  return res.data.data as ExplainerPreflight;
}

export async function importExplainer(initiativeId: string, bundle: ExplainerBundle, keepFields: string[]): Promise<ExplainerImportSummary> {
  const res = await api.post(
    `/admin/ballot-initiatives/${initiativeId}/explainer-import`,
    bundleForm(bundle, { keep_fields: keepFields.join(',') })
  );
  return res.data.data as ExplainerImportSummary;
}

export async function bulkExplainerImport(cycle: string, files: File[], dryRun: boolean): Promise<ExplainerBulkResponse> {
  const form = new FormData();
  for (const file of files) form.append('files', file);
  form.append('initiative_cycle', cycle);
  form.append('dry_run', dryRun ? 'true' : 'false');
  const res = await api.post('/admin/ballot-initiatives/explainer-import', form);
  return res.data.data as ExplainerBulkResponse;
}
